import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import { zonasDeTareas, claveDeTarea } from './tareasDeLaMinuta';
import {
  listarTareasDeMinuta, registrarTareaDeMinuta, olvidarTareaDeMinuta,
  listarFirmasDeMinuta
} from '../../services/minutasV2Service';
import { yaFirmaron, firmaDeLaFila, cuandoFirmo } from './firmasDelActa';
import { zonaDeFolio } from './folioDeMinuta';
import { zonaDeLugar } from './lugarDelActa';
import {
  zonaDeAsistentes, inicialesDe, puestoDeCandidatos,
  estadosDeAsistencia, rotuloDeAsistencia
} from './asistentesDelActa';
import {
  esEtiquetaDePersona, esEtiquetaDeTextoLargo, hayCampoDePersona, anchoDeColumna,
  esColumnaDePersona, columnaDeConsecutivo, renumerar
} from './camposDelActa';
import SelectorDePersona from './SelectorDePersona';
import { personasDelDirectorio, emparejarPersona } from '../../services/planner';
import ComboDeCatalogo from './ComboDeCatalogo';
import {
  esEtiquetaDeFecha, aISO, desdeISO, formaDe,
  esEtiquetaDeHora, aHora24, desdeHora24
} from './fechasDelActa';
import EnviarAPlanner from './EnviarAPlanner';

/**
 * ZonasDelActa — corregir a mano lo que el modelo dedujo de la reunión.
 *
 * El acta se rellena volcando datos en las zonas que tiene la plantilla, y este
 * panel enseña esas mismas zonas para poder tocarlas una a una. No es un editor
 * de texto sobre el informe: es el mismo mecanismo con el que se escribe el
 * .docx, abierto.
 *
 * Esa es la razón de que sea así y no un `contentEditable` encima del informe,
 * que habría sido más cómodo de escribir. Al guardar, el Word se reconstruye
 * desde estos valores. Si se editara el HTML, habría que deducir las zonas otra
 * vez a partir del texto editado, y el documento descargado dejaría de ser
 * exactamente lo revisado —que es justo lo que se pedía que no volviera a
 * pasar—.
 *
 * Cada forma de zona se edita como lo que es:
 *
 *   · `valor`  — un dato suelto: una línea.
 *   · `bloque` — texto largo con su estructura: varias líneas, donde `##` marca
 *                un subtítulo y `-` una viñeta. Son las mismas marcas que
 *                entiende el generador del .docx, así que lo que se escriba con
 *                ellas sale con el formato de la plantilla, no en negrita
 *                indiscriminada.
 *   · `lista`  — una tabla: una fila por compromiso, con sus columnas.
 *
 * La tabla de tareas se reconoce sola y gana un botón para llevarlas a Planner.
 * No es una sección aparte: son las mismas filas del acta, así que corregir
 * aquí el responsable corrige el Word y lo que se manda a Planner a la vez.
 */

const estilos = (C) => ({
  panel: {
    borderTop: `1px solid ${C.borde}`,
    background: C.fondoHondo,
    padding: '16px 26px 22px'
  },
  cabecera: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    gap: 12, flexWrap: 'wrap', marginBottom: 12, minHeight: 18
  },

  zona: { marginBottom: 16 },
  etiqueta: {
    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
    fontSize: 12.5, fontWeight: 600, color: C.suave, marginBottom: 5
  },
  aviso: { fontSize: 11.5, fontWeight: 400, color: C.atencion },
  pista: { fontSize: 11.5, fontWeight: 400, color: C.tenue },

  campo: {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 7,
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`,
    fontFamily: FUENTE, fontSize: 13.5
  },
  // Sin barra de desplazamiento y sin alto fijo: el campo mide lo que mida su
  // contenido.
  //
  // Con alto fijo, la zona de acuerdos de un acta larga se leía por una ventana
  // de seis renglones, desplazándose dentro de una página que a su vez se
  // desplaza. Y lo que no se ve no se revisa: el sentido de esta pantalla es
  // poder mirar el acta entera antes de publicarla.
  area: {
    minHeight: 132, resize: 'none', overflow: 'hidden', lineHeight: 1.55
  },
  // El objetivo de la reunión, que son dos frases y no un acta entera: más bajo
  // que un bloque, pero nunca de un solo renglón.
  parrafo: {
    minHeight: 62, resize: 'none', overflow: 'hidden', lineHeight: 1.55
  },
  // El calendario, el reloj y sus iconos los dibuja el navegador, y los saca
  // claros u oscuros según el `color-scheme` de la página —que el proveedor del
  // tema ya pone en el `body`—, así que aquí no hay que forzar nada. El ancho
  // tampoco: lo da la columna estrecha de la rejilla.
  campoFecha: {},
  // Lo que se lee pero no se escribe.
  campoFijo: {
    background: C.fondoHondo, color: C.suave, cursor: 'default',
    fontVariantNumeric: 'tabular-nums', letterSpacing: .3
  },

  tablaCaja: { overflowX: 'auto', border: `1px solid ${C.borde}`, borderRadius: 8, background: C.panel },
  tabla: { borderCollapse: 'collapse', width: '100%', minWidth: 520 },
  th: {
    textAlign: 'left', padding: '7px 9px', fontSize: 11.5, fontWeight: 600,
    color: C.tenue, borderBottom: `1px solid ${C.borde}`, whiteSpace: 'nowrap'
  },
  td: { padding: 4, borderBottom: `1px solid ${C.borde}`, verticalAlign: 'top' },
  celda: {
    width: '100%', boxSizing: 'border-box', padding: '5px 7px', borderRadius: 5,
    background: 'transparent', color: C.texto, border: '1px solid transparent',
    fontFamily: FUENTE, fontSize: 13, resize: 'vertical'
  },
  // La fecha de una fila: un calendario, no una caja de texto.
  fechaCelda: {
    width: '100%', boxSizing: 'border-box', padding: '5px 7px', borderRadius: 5,
    cursor: 'pointer', background: C.panelAlto, color: C.texto,
    border: `1px solid ${C.borde}`, fontFamily: FUENTE, fontSize: 12.5
  },
  // Lo que había escrito donde debería ir una fecha y no lo es.
  pendiente: { marginTop: 3, fontSize: 11.5, color: C.tenue, lineHeight: 1.4 },
  // La puntualidad: se elige entre los estados que la propia plantilla enumera.
  //
  // Un `<select>` de verdad y no una lista hecha a mano —que es lo que se hizo
  // para el lugar— porque aquí son cuatro opciones fijas y no hay nada que
  // buscar. La objeción de entonces era contra `<datalist>`, que ignora el tema
  // y sale blanco sobre la pantalla oscura; el desplegable de un `<select>` sí
  // respeta el `color-scheme` de la página, igual que el calendario y el reloj.
  combo: {
    padding: '5px 7px', borderRadius: 5, cursor: 'pointer',
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`,
    fontFamily: FUENTE, fontSize: 12.5, maxWidth: 220
  },
  // El asa para arrastrar una fila a otro sitio.
  agarre: {
    background: 'transparent', border: 'none', cursor: 'grab', padding: '4px 2px',
    color: C.tenue, fontSize: 14, lineHeight: 1, borderRadius: 5
  },
  // La fila que se está arrastrando, y aquella sobre la que va a caer.
  filaEnVuelo: { opacity: .4 },
  filaDestino: { background: C.panelAlto },
  // Una celda que se calcula, no se escribe: el numero de fila, las iniciales.
  // Se ve como lo que es —un dato, no un hueco— porque dejarla con pinta de
  // campo editable invita a escribir dentro y a preguntarse por que no deja.
  calculada: {
    padding: '5px 7px', fontSize: 12.5, color: C.suave, lineHeight: 1.4,
    fontFamily: FUENTE, whiteSpace: 'nowrap'
  },

  // La palomita de quien ya firmo. En verde: es la unica marca de la tabla que
  // dice que algo salio bien, y las demas celdas son campos que se editan.
  firmo: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 7px', fontSize: 12.5, lineHeight: 1.4,
    color: C.exito, fontFamily: FUENTE, whiteSpace: 'nowrap'
  },
  // El «Firmó» de al lado. Una palomita sola se lee como «marcado», que no es
  // lo mismo, y a ciegas no se lee de ninguna manera.
  firmoTexto: { fontSize: 11.5, color: C.tenue },
  // El enlace a la tarea que ya existe, bajo el texto del compromiso.
  vinculo: {
    display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap',
    marginTop: 4, fontSize: 11.5, color: C.tenue, lineHeight: 1.4
  },
  enlaceTarea: { color: C.acentoClaro, textDecoration: 'none', whiteSpace: 'nowrap' },
  desvincular: {
    background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
    color: C.tenue, fontSize: 11.5, fontFamily: FUENTE, textDecoration: 'underline'
  },
  quitar: {
    background: 'transparent', border: 'none', color: C.tenue, cursor: 'pointer',
    fontSize: 15, lineHeight: 1, padding: '4px 7px', borderRadius: 5
  },
  añadir: {
    marginTop: 7, padding: '5px 11px', borderRadius: 7, fontSize: 12.5,
    background: 'transparent', color: C.acentoClaro, fontFamily: FUENTE,
    border: `1px dashed ${C.borde}`, cursor: 'pointer'
  },

  estado: (malo) => ({ fontSize: 12.5, color: malo ? C.error : C.tenue, lineHeight: 1.5 })
});

/** Una fila nueva, vacía y del ancho de la tabla. */
const filaVacia = (columnas) => columnas.map(() => '');

/**
 * Cuánto ocupa cada zona en la rejilla.
 *
 * Tres anchos y no dos. Una fecha son diez caracteres y una hora cinco: darles
 * media fila —lo mismo que a «Objetivo de la reunión»— dejaba cajas con tres
 * cuartas partes de aire y estiraba la cabecera del acta a lo largo de varias
 * pantallas. Van a cuarto de fila, así que dos de ellas ocupan lo que antes
 * ocupaba una sola y quedan del mismo tamaño, una al lado de la otra.
 */
const claseDeZona = (region) => {
  if (region.tipo !== 'valor') return 'ancha';
  if (esEtiquetaDeFecha(region.etiqueta) || esEtiquetaDeHora(region.etiqueta)) return 'corta';
  // El objetivo va a renglón aparte y a todo lo ancho, como los textos largos:
  // en media fila se escribe por una mirilla que se desplaza sola.
  if (esEtiquetaDeTextoLargo(region.etiqueta)) return 'ancha';
  return undefined;
};

/**
 * Hace que una celda crezca con lo que contiene.
 *
 * Las celdas de tareas guardan frases enteras —«Revisar el contrato con el
 * proveedor antes del cierre»— y en una caja de una línea hay que leerlas
 * desplazándose. Aquí el alto lo pone el contenido.
 */
const acomodar = (elemento) => {
  if (!elemento) return;
  elemento.style.height = 'auto';
  elemento.style.height = `${elemento.scrollHeight}px`;
};

/**
 * @param {Object} props
 * @param {Array} props.regiones - las zonas del acta, con tipo y columnas
 * @param {Object} props.valores - lo que hay escrito en cada una
 * @param {Function} props.onGuardar - recibe los valores corregidos
 * @param {boolean} props.guardando
 * @param {string|null} props.error - lo que haya fallado al guardar
 * @param {Array} props.sinDato - zonas que quedaron en blanco al generar
 * @param {Date|null} props.fechaReunion - para completar los años de las fechas
 * @param {Function} props.tokenGraph - da un token de Microsoft Graph
 * @param {number|null} props.minuteId - para saber qué tareas ya se mandaron
 * @param {string|null} props.userId
 * @param {string} props.titulo - el de la reunión, para la nota de la tarea
 */
const ZonasDelActa = ({
  regiones = [], valores = {}, onGuardar, guardando = false, error = null,
  sinDato = [], fechaReunion = null, tokenGraph = null, titulo = '',
  onEstado = null, folio = null, minuteId = null, userId = null,
  catalogoLugares = [], onCrearLugar = null, onRenombrarLugar = null, onOlvidarLugar = null
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [borrador, setBorrador] = useState(valores);
  const [planner, setPlanner] = useState(false);

  // ── Arrastrar una fila a otro sitio ───────────────────────────────────
  //
  // `agarrada` existe porque la fila NO puede ser arrastrable todo el rato: con
  // `draggable` puesto, seleccionar texto dentro de una celda arrastra la fila
  // en vez de seleccionar. Solo se vuelve arrastrable mientras se tiene pulsada
  // el asa, que es lo único de la fila que no es un campo.
  const [agarrada, setAgarrada] = useState(null);
  const [enVuelo, setEnVuelo] = useState(null);
  const [destino, setDestino] = useState(null);

  const soltarArrastre = useCallback(() => {
    setAgarrada(null); setEnVuelo(null); setDestino(null);
  }, []);

  // ── La gente del directorio, para la tabla de asistentes ──────────────
  //
  // Se pide una sola vez y solo si el acta tiene esa tabla: en un acta sin
  // asistentes seria bajar cientos de fichas para nada.
  //
  // Si falla no se dice: significa que no esta concedido el permiso de leer el
  // directorio, y entonces los nombres se escriben como siempre. Un error rojo
  // por no poder ofrecer una comodidad estorbaria mas de lo que ayuda.
  const [gente, setGente] = useState([]);

  const asistentes = useMemo(() => zonaDeAsistentes(regiones), [regiones]);

  // Las tablas de tareas del acta. En plural: un acta suele traer la de
  // acuerdos y un «Parking Lot» de temas pendientes, y las dos son compromisos
  // de alguien. Se buscan sobre las zonas y no sobre el borrador: su forma no
  // cambia al editar el contenido.
  const tareas = useMemo(() => zonasDeTareas(regiones), [regiones]);

  // ── Las tareas que esta acta ya mandó a Planner ───────────────────────
  //
  // Se traen al abrir, no al pulsar «crear». El enlace de una tarea que ya
  // existe es información del acta: quien la abre para leerla necesita poder
  // ir a ella sin descubrirlo dentro de un diálogo de creación.
  const [tareasCreadas, setTareasCreadas] = useState([]);

  const refrescarTareas = useCallback(async () => {
    if (!minuteId || !userId) return;
    try {
      setTareasCreadas(await listarTareasDeMinuta(minuteId, userId));
    } catch {
      // Sin vínculos se sigue pudiendo trabajar: lo que se pierde es saber que
      // algo ya se mandó, y decirlo en rojo sobre el acta sería alarmar por una
      // comodidad.
      setTareasCreadas([]);
    }
  }, [minuteId, userId]);

  useEffect(() => { refrescarTareas(); }, [refrescarTareas]);

  // ── Quién ha firmado ya ───────────────────────────────────────────────
  //
  // La rúbrica dibujada va al documento, pero aquí la columna «Firma» se veía
  // igual de vacía antes y después de firmar: no había forma de saber por dónde
  // iba la ronda sin abrir el documento o el diálogo de firmas. Con esto, la
  // fila de quien ya firmó lo dice.
  const [firmados, setFirmados] = useState([]);

  const refrescarFirmas = useCallback(async () => {
    if (!minuteId || !userId) return;
    try {
      const { rounds } = await listarFirmasDeMinuta(minuteId, userId);
      setFirmados(yaFirmaron(rounds));
    } catch {
      // Sin esto se sigue trabajando igual: lo que se pierde es la palomita.
      // Un acta no se deja de corregir porque no se sepa quién firmó.
      setFirmados([]);
    }
  }, [minuteId, userId]);

  useEffect(() => { refrescarFirmas(); }, [refrescarFirmas]);

  const apuntarTarea = useCallback(async (tarea) => {
    if (!minuteId || !userId) return null;
    return registrarTareaDeMinuta(minuteId, userId, tarea);
  }, [minuteId, userId]);

  /**
   * Deja de dar por hecho que la tarea existe.
   *
   * Para cuando alguien la borró en Planner. Sin esto, el acta se quedaría
   * enseñando un enlace muerto y negándose para siempre a crear la que sí hace
   * falta. No borra nada allí: esta aplicación no manda en ese plan.
   */
  const olvidarTarea = useCallback(async (tareaId) => {
    if (!minuteId || !userId) return;
    setTareasCreadas(t => t.filter(x => x.tareaId !== tareaId));
    try {
      await olvidarTareaDeMinuta(minuteId, userId, tareaId);
    } catch {
      refrescarTareas();
    }
  }, [minuteId, userId, refrescarTareas]);

  // Por el texto del compromiso, no por la fila: las filas se mueven.
  const tareaDeFila = useCallback((texto) => {
    const clave = claveDeTarea(texto);
    return tareasCreadas.find(t => t.clave === clave) || null;
  }, [tareasCreadas]);

  // Cuántos compromisos de la tabla ya tienen tarea. Se cuenta aquí arriba y
  // no al pintar la tabla porque ahora también lo necesita la barra de menús,
  // que vive en otra pantalla.
  const tareasYaHechas = useMemo(() => {
    let hechas = 0;
    for (const zona of tareas) {
      const filas = Array.isArray(borrador[zona.region.id]) ? borrador[zona.region.id] : [];
      hechas += filas.filter(f => tareaDeFila(f[zona.columnas.titulo])).length;
    }
    return hechas;
  }, [tareas, borrador, tareaDeFila]);

  /** La tabla de tareas a la que pertenece una región, si es una de ellas. */
  const zonaTareaDe = useCallback(
    (regionId) => tareas.find(z => z.region.id === regionId) || null,
    [tareas]
  );

  // Lo que se le manda al diálogo de Planner: cada tabla de tareas con sus
  // filas de AHORA. Memorizado porque el diálogo guarda lo elegido en cada fila
  // indexado por tabla, y una lista nueva en cada pintada le borraría al vuelo
  // lo que la persona acabara de elegir.
  const zonasParaPlanner = useMemo(() => tareas.map(z => ({
    id: z.region.id,
    titulo: z.region.etiqueta || z.region.titulo || 'Tareas',
    filas: Array.isArray(borrador[z.region.id]) ? borrador[z.region.id] : [],
    columnas: z.columnas
  })), [tareas, borrador]);

  // Lo que decide si hay que bajar el directorio es SI existe la tabla, no cual
  // es. `regiones` se reemplaza entera cada vez que se guarda el acta, asi que
  // `asistentes` es un objeto nuevo en cada guardado; con el en las
  // dependencias, cada correccion volvia a bajar cientos de fichas para acabar
  // con la misma lista.
  const hayAsistentes = Boolean(asistentes);
  // Y tambien si la cabecera tiene «Convocado por»: es el mismo directorio, la
  // misma razon y —por lo mismo de arriba— tiene que ser un booleano.
  // Y para cualquier columna que pida un nombre: los responsables de los
  // acuerdos, los del parking lot. Es la misma gente en todas.
  const hayQuienElegir = hayAsistentes
    || hayCampoDePersona(regiones)
    || regiones.some(r => r && r.tipo === 'lista'
      && (r.columnas || []).some(esColumnaDePersona));

  useEffect(() => {
    if (!hayQuienElegir || !tokenGraph) return undefined;
    let vigente = true;
    (async () => {
      try {
        const lista = await personasDelDirectorio(await tokenGraph());
        if (vigente) setGente(lista);
      } catch {
        if (vigente) setGente([]);
      }
    })();
    return () => { vigente = false; };
  }, [hayQuienElegir, tokenGraph]);

  // ── El puesto se trae del directorio, no se teclea ────────────────────
  //
  // El nombre de los asistentes ya viene puesto cuando se abre el acta: sale de
  // la cita del calendario. Por eso nadie pasa por el desplegable, y por eso el
  // cargo se quedaba en blanco aunque el directorio lo supiera — hasta ahora
  // solo se rellenaba al elegir a alguien a mano, que es justo lo que no hace
  // falta hacer cuando el nombre ya está.
  //
  // Solo se tocan las celdas vacías. Lo que alguien escribió a mano manda: en
  // un acta se pone «Director Jurídico (invitado)» o el cargo que tenía cuando
  // se celebró la reunión, y el directorio no sabe nada de eso.
  //
  // Una sola pasada por acta. Si se repitiera, vaciar a propósito una celda no
  // serviría de nada: volvería a llenarse sola y no habría forma de dejarla en
  // blanco.
  const yaSeCompletaron = useRef(null);

  useEffect(() => {
    if (!asistentes || gente.length === 0) return;
    const { region, columnas } = asistentes;
    if (columnas.puesto < 0 || columnas.nombre < 0) return;

    const pasada = `${region.id}:${gente.length}`;
    if (yaSeCompletaron.current === pasada) return;
    yaSeCompletaron.current = pasada;

    setBorrador(b => {
      const tabla = Array.isArray(b[region.id]) ? b[region.id] : null;
      if (!tabla) return b;

      let hubo = false;
      const nueva = tabla.map(fila => {
        const nombre = String(fila[columnas.nombre] || '').trim();
        if (!nombre) return fila;
        if (String(fila[columnas.puesto] || '').trim()) return fila;

        const { candidatos } = emparejarPersona(nombre, gente);
        const puesto = puestoDeCandidatos(candidatos);
        if (!puesto) return fila;

        hubo = true;
        const copia = fila.slice();
        copia[columnas.puesto] = puesto;
        return copia;
      });

      // Sin cambios, el mismo objeto: devolver una copia dispararía el guardado
      // automático y archivaría una versión del acta idéntica a la anterior.
      return hubo ? { ...b, [region.id]: nueva } : b;
    });
  }, [asistentes, gente]);

  // Si NADIE del directorio trae cargo, no es que la empresa no los tenga: es
  // que no nos los están dando. Se dice donde se nota —encima de la tabla— en
  // vez de dejar una columna vacía sin explicación.
  const directorioSinPuestos = gente.length > 0 && gente.every(p => !p.puesto);

  // ── Deshacer ──────────────────────────────────────────────────────────
  //
  // Guardar es automático, así que ya no hay un momento en el que alguien
  // decida «esto queda así». Deshacer es lo que sustituye a esa decisión: sin
  // ello, un borrado accidental se guardaría solo y no habría vuelta.
  //
  // Se guardan estados enteros y no cambios sueltos. Un acta son catorce zonas
  // de texto y un par de tablas; copiarlas cuesta nada y evita todo el
  // andamiaje de invertir operaciones, que es donde viven los fallos raros.
  const [pila, setPila] = useState([]);
  const [futuro, setFuturo] = useState([]);
  // Escribir una palabra son ocho pulsaciones y un solo cambio para quien la
  // escribe. Sin agrupar, deshacer iría letra a letra.
  const ultimoApunte = useRef(0);
  const AGRUPAR = 700;

  const apuntar = useCallback((anterior) => {
    const ahora = Date.now();
    const seguido = ahora - ultimoApunte.current < AGRUPAR;
    ultimoApunte.current = ahora;
    if (seguido) return;
    setPila(p => [...p.slice(-49), anterior]);
    setFuturo([]);
  }, []);

  const deshacer = useCallback(() => {
    setPila(p => {
      if (!p.length) return p;
      const previo = p[p.length - 1];
      setBorrador(actual => { setFuturo(f => [...f, actual]); return previo; });
      // Lo siguiente que se escriba abre un apunte nuevo y no se agrupa con lo
      // que había antes de deshacer.
      ultimoApunte.current = 0;
      return p.slice(0, -1);
    });
  }, []);

  const rehacer = useCallback(() => {
    setFuturo(f => {
      if (!f.length) return f;
      const siguiente = f[f.length - 1];
      setBorrador(actual => { setPila(p => [...p, actual]); return siguiente; });
      ultimoApunte.current = 0;
      return f.slice(0, -1);
    });
  }, []);

  // Cuántas veces se ha dado por terminado algo, y cuántas de ellas se han
  // atendido ya. Un contador y no un booleano: con un booleano la bandera se
  // quedaba levantada, y a partir del primer campo terminado TODO se guardaría
  // a los 120 ms — que es volver a guardar por tecla.
  const [terminados, setTerminados] = useState(0);
  const atendidos = useRef(0);

  /** Lo de este campo ya está: guárdalo sin esperar al reloj. */
  const terminado = useCallback(() => setTerminados(n => n + 1), []);

  const cambiar = useCallback((id, valor) => {
    setBorrador(b => { apuntar(b); return { ...b, [id]: valor }; });
  }, [apuntar]);

  /**
   * Deja la tabla como queda, con su consecutivo al día.
   *
   * Todo lo que cambia el número o el orden de las filas —añadir, quitar,
   * arrastrar— pasa por aquí. Renumerar en cada sitio por separado era
   * garantizar que uno se quedaría sin hacerlo, y el que se queda sin hacerlo
   * es el que manda al Word una tabla numerada 1, 3, 4.
   */
  const ponerFilas = useCallback((region, columnas, filas) => {
    const consecutiva = columnaDeConsecutivo(columnas, filas);
    cambiar(region.id, consecutiva >= 0 ? renumerar(filas, consecutiva) : filas);
    // Añadir, quitar o mover una fila no se escribe letra a letra: cuando
    // ocurre, ya está terminado.
    terminado();
  }, [cambiar, terminado]);

  /** Mueve una fila a otra posición. Arrastrando, o con las flechas. */
  const moverFila = useCallback((region, columnas, filas, desde, hasta) => {
    if (desde == null || hasta == null || desde === hasta) return;
    if (hasta < 0 || hasta >= filas.length) return;
    const nuevas = filas.slice();
    const [movida] = nuevas.splice(desde, 1);
    nuevas.splice(hasta, 0, movida);
    ponerFilas(region, columnas, nuevas);
  }, [ponerFilas]);

  const cambiarCelda = useCallback((id, fila, columna, valor) => {
    setBorrador(b => {
      const tabla = Array.isArray(b[id]) ? b[id].map(f => f.slice()) : [];
      if (!tabla[fila]) return b;
      apuntar(b);
      tabla[fila][columna] = valor;
      return { ...b, [id]: tabla };
    });
  }, [apuntar]);

  /**
   * Apunta a alguien en la tabla de asistentes.
   *
   * Las tres columnas a la vez y en un solo cambio: el nombre, las iniciales
   * que salen de el y el puesto que dice el directorio. Por separado serian
   * tres apuntes en el historial de deshacer para lo que la persona vivio como
   * un solo acto — elegir a alguien—, y deshacerlo pediria tres pasos.
   *
   * El puesto solo se pisa si viene: hay cuentas del directorio sin cargo, y
   * borrar lo que alguien escribio a mano para dejarlo en blanco es peor que no
   * tocarlo.
   */
  const ponerAsistente = useCallback((filaIndice, persona, texto) => {
    if (!asistentes) return;
    // Elegir a alguien es un acto completo, no media palabra.
    terminado();
    const { region, columnas } = asistentes;
    const nombre = persona ? persona.nombre : (texto || '');

    setBorrador(b => {
      const tabla = Array.isArray(b[region.id]) ? b[region.id].map(f => f.slice()) : [];
      if (!tabla[filaIndice]) return b;
      apuntar(b);

      tabla[filaIndice][columnas.nombre] = nombre;
      if (columnas.iniciales >= 0) tabla[filaIndice][columnas.iniciales] = inicialesDe(nombre);
      if (columnas.puesto >= 0 && persona && persona.puesto) {
        tabla[filaIndice][columnas.puesto] = persona.puesto;
      }

      return { ...b, [region.id]: tabla };
    });
  }, [asistentes, apuntar, terminado]);

  const tocado = useMemo(
    () => JSON.stringify(borrador) !== JSON.stringify(valores),
    [borrador, valores]
  );

  // ── Guardado automático ───────────────────────────────────────────────
  //
  // Guardar es lo que rehace el .docx, y rehacer el .docx es lo que obliga a
  // recargar la vista del documento. Así que CUÁNDO se guarda no es un detalle
  // de rendimiento: decide cuántas veces se recarga el acta delante de quien la
  // está corrigiendo.
  //
  // Por tecla, ni hablar. Pero por reloj tampoco, que es como estaba: un
  // temporizador salta a mitad de una frase —en cuanto alguien se para a
  // pensar— y entonces el documento se recarga mientras se escribe dentro del
  // campo. Ningún número arregla eso; el problema es que el reloj no sabe nada
  // de lo que está haciendo la persona.
  //
  // Se guarda cuando un campo SE TERMINA. Salir de él —con el tabulador, con un
  // clic en otro sitio— es la señal de que lo escrito ya está completo, y la da
  // la persona, no un cronómetro. Lo que no se escribe letra a letra —elegir
  // del desplegable, poner una fecha, mover una fila— se guarda al instante:
  // ahí el cambio ya está terminado en el momento en que ocurre.
  //
  // El reloj se queda, pero largo y como red: para quien deja un campo a medias
  // y se va a otra ventana. Ya no es el mecanismo, es el seguro.
  const ESPERA = 20000;
  const PRONTO = 120;
  const enviado = useRef(JSON.stringify(valores));

  useEffect(() => {
    if (!tocado || guardando) return undefined;
    const texto = JSON.stringify(borrador);
    // Ya se mandó tal cual. Sin esto, salir de un campo sin haberlo tocado
    // volvería a guardar y a rehacer el documento para nada.
    if (texto === enviado.current) return undefined;

    const apura = terminados > atendidos.current;
    const t = setTimeout(() => {
      atendidos.current = terminados;
      enviado.current = texto;
      onGuardar(borrador);
    }, apura ? PRONTO : ESPERA);
    return () => clearTimeout(t);
  }, [borrador, tocado, guardando, onGuardar, terminados]);

  // Irse a otra ventana también es terminar.
  //
  // Sin esto, el seguro de veinte segundos sería el único que queda para quien
  // escribe media frase y se va a Teams: lo escrito viviría solo en esta
  // pestaña hasta que volviera. Perder el foco de la ventana es la misma señal
  // que salir de un campo, y llega antes.
  useEffect(() => {
    const fuera = () => terminado();
    window.addEventListener('blur', fuera);
    return () => window.removeEventListener('blur', fuera);
  }, [terminado]);

  // Cuando vuelven valores nuevos —de guardar, o de regenerar el acta— el
  // borrador tiene que partir de ellos.
  //
  // Pero solo si no se ha seguido escribiendo mientras tanto. Con guardado
  // automático eso pasa constantemente: se guarda, y mientras el servidor
  // responde la persona sigue tecleando. Adoptar la respuesta sin mirar le
  // borraría de la pantalla lo que acaba de escribir.
  useEffect(() => {
    const llegado = JSON.stringify(valores);
    setBorrador(actual => (JSON.stringify(actual) === enviado.current ? valores : actual));
    enviado.current = llegado;
  }, [valores]);

  // Ctrl+Z deshace, Ctrl+Shift+Z y Ctrl+Y rehacen. Ctrl+S ya no guarda porque
  // no hay nada que guardar a mano; se atiende igual para que el navegador no
  // ofrezca guardar la página, que es lo que haría si no.
  useEffect(() => {
    const alPulsar = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tecla = e.key.toLowerCase();

      if (tecla === 'z') { e.preventDefault(); if (e.shiftKey) rehacer(); else deshacer(); }
      else if (tecla === 'y') { e.preventDefault(); rehacer(); }
      else if (tecla === 's') { e.preventDefault(); }
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [deshacer, rehacer]);

  // Cerrar con un guardado aún en vuelo se lo llevaría: el temporizador muere
  // con la pestaña.
  useEffect(() => {
    if (!tocado && !guardando) return undefined;
    const alSalir = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [tocado, guardando]);

  const [guardadoEn, setGuardadoEn] = useState(null);
  const estabaGuardando = useRef(false);
  useEffect(() => {
    if (estabaGuardando.current && !guardando && !error) setGuardadoEn(new Date());
    estabaGuardando.current = guardando;
  }, [guardando, error]);

  // Quien pinta los menús necesita saber si hay algo que deshacer. Se avisa
  // desde un efecto y no durante el pintado: avisar mientras se pinta provoca
  // que el padre se repinte a media pasada.
  const avisar = useRef(onEstado);
  useEffect(() => { avisar.current = onEstado; }, [onEstado]);
  useEffect(() => {
    if (avisar.current) {
      avisar.current({
        deshacer, rehacer,
        puedeDeshacer: pila.length > 0,
        puedeRehacer: futuro.length > 0,
        guardando, pendiente: tocado,
        // Lo de Planner viaja por aquí por lo mismo que deshacer: el botón se
        // pinta arriba, en la barra, y quien sabe si hay tabla de tareas y
        // cuántas están hechas es este panel.
        hayTareas: Boolean(tareas.length > 0 && tokenGraph),
        tareasHechas: tareasYaHechas,
        abrirPlanner: () => setPlanner(true)
      });
    }
  }, [pila.length, futuro.length, guardando, tocado, deshacer, rehacer,
    tareas, tokenGraph, tareasYaHechas]);

  // Volver a medir los campos cuando el contenido llega de fuera.
  //
  // El `ref` que ajusta el alto solo corre al montar el campo. Eso basta cuando
  // se escribe, pero no cuando el acta se regenera, se deshace un cambio o
  // vuelve del servidor: el texto cambia, el alto se queda como estaba y
  // reaparece la barra de desplazamiento que se acaba de quitar.
  const panel = useRef(null);
  useEffect(() => {
    if (!panel.current) return;
    panel.current.querySelectorAll('textarea').forEach(acomodar);
  }, [borrador]);

  const enBlanco = useMemo(() => new Set(sinDato || []), [sinDato]);

  // El número de minuta: la casilla está, pero la llena el sistema.
  const campoFolio = useMemo(() => zonaDeFolio(regiones), [regiones]);

  // El lugar: texto libre, pero con lo ya usado a mano.
  const campoLugar = useMemo(() => zonaDeLugar(regiones), [regiones]);

  // Lo que se puede poner en la columna de puntualidad, según su leyenda.
  const estadosAsistencia = useMemo(() => {
    if (!asistentes || asistentes.columnas.asistencia < 0) return [];
    const cabecera = asistentes.region.columnas[asistentes.columnas.asistencia];
    return estadosDeAsistencia(cabecera);
  }, [asistentes]);

  if (!regiones.length) return null;

  const editorDeZona = (region) => {
    const valor = borrador[region.id];

    if (region.tipo === 'lista') {
      const columnas = region.columnas || [];
      const filas = Array.isArray(valor) ? valor : [];

      // Con qué forma se escriben las fechas de ESTA tabla: la de la primera
      // que ya esté puesta. El acta es de quien la escribió —si sus fechas
      // dicen «30/sep/2026», elegir una en el calendario no debe convertirla
      // en «30 de septiembre de 2026» y dejar la columna a dos estilos.
      let formaDeFecha = 'larga';
      for (const f2 of filas) {
        const i = columnas.findIndex(
          (cab, j) => esEtiquetaDeFecha(cab) && aISO(String(f2[j] || ''))
        );
        if (i >= 0) { formaDeFecha = formaDe(String(f2[i])); break; }
      }

      // ── La tabla de asistentes se edita distinto ────────────────────
      //
      // Es la unica del acta cuyas cuatro primeras columnas se saben sin
      // preguntar: el numero es la posicion, el nombre esta en el directorio de
      // la empresa, las iniciales salen del nombre y el puesto lo dice Entra.
      // Tecleadas a mano salen mal de tres maneras distintas —numeros repetidos
      // al borrar una fila, iniciales que se quedan viejas al corregir un
      // apellido, cargos desactualizados— y ninguna se nota al escribirla.
      const zonaTarea = zonaTareaDe(region.id);
      const esAsistentes = asistentes && asistentes.region.id === region.id;
      const cols = esAsistentes ? asistentes.columnas : null;

      // Qué columna es el consecutivo de ESTA tabla, si es que alguna lo es.
      const consecutiva = columnaDeConsecutivo(columnas, filas);
      return (
        <>
          <div style={S.tablaCaja}>
            <table style={S.tabla}>
              {/* Las columnas que se saben cortas piden su medida; las demás
                  —el acuerdo, el puesto— se reparten lo que sobra. Sin esto el
                  navegador da lo mismo a todas, porque lo que hay en cada celda
                  es un campo de texto y un campo de texto no tiene ancho
                  natural: pide igual para «1» que para tres renglones. */}
              <colgroup>
                <col style={{ width: 26 }} />
                {columnas.map((c, i) => {
                  const ancho = anchoDeColumna(c);
                  return <col key={i} style={ancho ? { width: ancho } : undefined} />;
                })}
                <col style={{ width: 34 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 26 }} aria-label="Orden" />
                  {columnas.map((c, i) => (
                    // La leyenda de la puntualidad no se pierde: sigue impresa
                    // en el Word, que es donde explica los códigos a quien
                    // recibe el acta, y aquí queda al pasar el ratón. Lo que no
                    // hace ya es ocupar media tabla para decir lo que ahora se
                    // lee dentro del desplegable.
                    <th
                      key={i}
                      style={S.th}
                      title={cols && i === cols.asistencia ? c : undefined}
                    >
                      {cols && i === cols.asistencia ? rotuloDeAsistencia(c) : c}
                    </th>
                  ))}
                  <th style={{ ...S.th, width: 34 }} aria-label="Quitar" />
                </tr>
              </thead>
              <tbody>
                {filas.map((fila, f) => (
                  <tr
                    key={f}
                    draggable={Boolean(agarrada && agarrada.id === region.id && agarrada.fila === f)}
                    style={{
                      ...(enVuelo && enVuelo.id === region.id && enVuelo.fila === f
                        ? S.filaEnVuelo : null),
                      ...(destino && destino.id === region.id && destino.fila === f
                        ? S.filaDestino : null)
                    }}
                    onDragStart={(ev) => {
                      ev.dataTransfer.effectAllowed = 'move';
                      // Firefox no empieza el arrastre sin algo que llevar.
                      ev.dataTransfer.setData('text/plain', String(f));
                      setEnVuelo({ id: region.id, fila: f });
                    }}
                    onDragOver={(ev) => {
                      if (!enVuelo || enVuelo.id !== region.id) return;
                      // Sin esto el navegador no admite la soltada.
                      ev.preventDefault();
                      ev.dataTransfer.dropEffect = 'move';
                      if (!destino || destino.fila !== f) setDestino({ id: region.id, fila: f });
                    }}
                    onDrop={(ev) => {
                      ev.preventDefault();
                      if (enVuelo && enVuelo.id === region.id) {
                        moverFila(region, columnas, filas, enVuelo.fila, f);
                      }
                      soltarArrastre();
                    }}
                    onDragEnd={soltarArrastre}
                  >
                    {/* El asa. Es lo único de la fila que no es un campo, y por
                        eso es lo que se agarra: con la fila arrastrable todo el
                        rato, seleccionar texto en una celda la arrastraría.
                        Con las flechas también, para quien no use ratón. */}
                    <td style={S.td}>
                      <button
                        type="button"
                        style={S.agarre}
                        title="Arrastrar para reordenar, o flechas arriba y abajo"
                        aria-label={`Mover la fila ${f + 1} de ${filas.length}`}
                        onMouseDown={() => setAgarrada({ id: region.id, fila: f })}
                        onMouseUp={() => setAgarrada(null)}
                        onKeyDown={(ev) => {
                          if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
                          ev.preventDefault();
                          moverFila(region, columnas, filas, f, f + (ev.key === 'ArrowUp' ? -1 : 1));
                        }}
                      >
                        ⠿
                      </button>
                    </td>

                    {columnas.map((_, c) => {
                      // El consecutivo es la posición de la fila, no un dato
                      // que alguien escriba. Tecleado a mano bastaba borrar una
                      // fila del medio —o moverla, ahora que se puede— para que
                      // el acta saliera numerada 1, 3, 4.
                      //
                      // Solo cuando lo que hay escrito son números: si la
                      // plantilla usa «Id» para códigos como «ACU-01»,
                      // renumerar sería borrarlos.
                      if (c === consecutiva) {
                        return <td key={c} style={S.td}><div style={S.calculada}>{f + 1}</div></td>;
                      }

                      // Las iniciales salen del nombre. Escritas aparte, se
                      // quedaban viejas en cuanto alguien corregia un apellido.
                      if (cols && c === cols.iniciales) {
                        return (
                          <td key={c} style={S.td}>
                            <div style={S.calculada}>{inicialesDe(fila[cols.nombre] || '')}</div>
                          </td>
                        );
                      }

                      // La puntualidad se elige. Escrita a mano salía «(P)»,
                      // «P», «Puntual» y «si» en la misma columna, y entonces
                      // la leyenda del acta deja de explicar lo que hay debajo.
                      if (cols && c === cols.asistencia && estadosAsistencia.length > 0) {
                        const marcado = String(fila[c] || '').trim();
                        const deLaLeyenda = estadosAsistencia.some(e => e.valor === marcado);
                        return (
                          <td key={c} style={S.td}>
                            <select
                              style={S.combo}
                              value={marcado}
                              aria-label={`Asistencia de la fila ${f + 1}`}
                              onChange={(e) => {
                                cambiarCelda(region.id, f, c, e.target.value);
                                terminado();
                              }}
                            >
                              <option value="">—</option>
                              {estadosAsistencia.map(e => (
                                <option key={e.codigo} value={e.valor}>
                                  {e.texto ? `${e.valor} ${e.texto}` : e.valor}
                                </option>
                              ))}
                              {/* Lo que ya había escrito y no es ninguno de la
                                  leyenda se queda como opción: dejarlo fuera lo
                                  borraría del acta al primer clic en cualquier
                                  otra celda, y en silencio. */}
                              {marcado && !deLaLeyenda && (
                                <option value={marcado}>{marcado}</option>
                              )}
                            </select>
                          </td>
                        );
                      }

                      // ── La columna «Firma» ────────────────────────
                      //
                      // Quien ya firmo lleva palomita; quien no, su celda de
                      // siempre.
                      //
                      // Y eso ultimo no es por prudencia: la columna «Firma» se
                      // deja en blanco a proposito en las actas que se imprimen
                      // para firmar a mano. Convertirla en un indicador de solo
                      // lectura habria quitado esa posibilidad a quien no usa
                      // la ronda de firmas, que son las actas de siempre.
                      if (cols && c === cols.firma) {
                        const suya = firmaDeLaFila(fila[cols.nombre], firmados);
                        if (suya) {
                          const dia = cuandoFirmo(suya.cuando);
                          return (
                            <td key={c} style={S.td}>
                              <div
                                style={S.firmo}
                                title={dia
                                  ? `${suya.nombre} firmó el ${dia}. Su rúbrica va en el documento.`
                                  : `${suya.nombre} firmó. Su rúbrica va en el documento.`}
                              >
                                <span aria-hidden="true">✓</span>
                                <span style={S.firmoTexto}>Firmó</span>
                              </div>
                            </td>
                          );
                        }
                      }

                      // El nombre se elige del directorio. Con texto libre:
                      // a una reunion va gente de fuera de la empresa, y una
                      // lista cerrada dejaria sin manera de apuntarla.
                      if (cols && c === cols.nombre && gente.length > 0) {
                        return (
                          <td key={c} style={S.td}>
                            <SelectorDePersona
                              personas={gente}
                              valor=""
                              textoMostrado={fila[c] || ''}
                              textoLibre
                              vacio="Elegir persona"
                              etiqueta={`Asistente de la fila ${f + 1}`}
                              onCambio={(id, persona) => ponerAsistente(f, persona, '')}
                            />
                          </td>
                        );
                      }

                      // La fecha se elige de un calendario, y puede quedarse
                      // pendiente. Escrita a mano salían «30/09», «30 sep» y
                      // «finales de mes» en la misma columna: ninguna de las
                      // tres se entiende igual al leer el acta, y la última no
                      // sirve para ponerle plazo a la tarea de Planner.
                      //
                      // Vacía es «pendiente», y es un estado legítimo: un
                      // acuerdo cuya fecha no se acordó no debe obligar a nadie
                      // a inventarse una para poder seguir.
                      if (esEtiquetaDeFecha(columnas[c])) {
                        const escrito = String(fila[c] || '').trim();
                        const iso = aISO(escrito);
                        return (
                          <td key={c} style={S.td}>
                            <input
                              type="date"
                              style={S.fechaCelda}
                              value={iso}
                              aria-label={`Fecha de la fila ${f + 1}`}
                              onChange={(e) => {
                                cambiarCelda(
                                  region.id, f, c,
                                  e.target.value ? desdeISO(e.target.value, formaDeFecha) : ''
                                );
                                terminado();
                              }}
                            />
                            {/* Lo que ponía y no es una fecha —«Por definir»,
                                «al cierre del mes»— no desaparece de la
                                pantalla por haber cambiado la caja por un
                                calendario. Se queda a la vista hasta que
                                alguien elija un día, que es lo contrario de
                                borrarlo en silencio. */}
                            {!iso && escrito && (
                              <div style={S.pendiente}>{escrito}</div>
                            )}
                          </td>
                        );
                      }

                      // Cualquier columna que pida un nombre se elige del
                      // directorio: el responsable de un acuerdo, el del
                      // parking lot, el de la tabla que traiga la plantilla de
                      // cada quien. No se mira de qué tabla es —un acta trae
                      // tres o cuatro y todas apuntan a la misma gente—, se
                      // mira qué dice la cabecera de la columna.
                      //
                      // Escrito a mano salía «Iván P.», que contra un
                      // directorio de ochocientas personas no distingue a
                      // nadie: al mandar el acuerdo a Planner había que volver
                      // a elegirlo, o la tarea se creaba sin responsable.
                      // Elegido aquí, el nombre que queda en el acta ya es el
                      // del directorio.
                      //
                      // Con texto libre, como el de asistentes: a un acuerdo se
                      // le pone «Iván y Jorge», o alguien de fuera, y una lista
                      // cerrada dejaría sin manera de escribirlo.
                      if (esColumnaDePersona(columnas[c]) && gente.length > 0) {
                        return (
                          <td key={c} style={S.td}>
                            <SelectorDePersona
                              personas={gente}
                              valor=""
                              textoMostrado={fila[c] || ''}
                              textoLibre
                              vacio="Elegir persona"
                              etiqueta={`${columnas[c]}, fila ${f + 1}`}
                              onCambio={(id, persona) => {
                                cambiarCelda(region.id, f, c, persona ? persona.nombre : '');
                                terminado();
                              }}
                            />
                          </td>
                        );
                      }

                      // El compromiso que ya tiene tarea la enseña aquí, en su
                      // fila. Es donde se está mirando cuando surge la pregunta
                      // —«¿esto se mandó?»— y donde el enlace sirve de algo.
                      const suTarea = (zonaTarea && c === zonaTarea.columnas.titulo)
                        ? tareaDeFila(fila[c])
                        : null;

                      return (
                        <td key={c} style={S.td}>
                          <textarea
                            style={{ ...S.celda, minHeight: 30, overflow: 'hidden' }}
                            rows={1}
                            ref={acomodar}
                            value={fila[c] || ''}
                            onChange={(e) => { cambiarCelda(region.id, f, c, e.target.value); acomodar(e.target); }}
                            onBlur={terminado}
                          />
                          {suTarea && (
                            <div style={S.vinculo}>
                              <a href={suTarea.url} target="_blank" rel="noreferrer" style={S.enlaceTarea}>
                                ↗ Ver en Planner
                              </a>
                              {suTarea.plan && <span> · {suTarea.plan}</span>}
                              {/* Alguien pudo borrarla por el otro lado. Sin
                                  esta salida, el acta se quedaría con un enlace
                                  muerto y sin poder crear la que hace falta. */}
                              <button
                                style={S.desvincular}
                                title="La tarea ya no existe en Planner: desvincularla para poder crearla otra vez"
                                onClick={() => olvidarTarea(suTarea.tareaId)}
                              >
                                desvincular
                              </button>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td style={S.td}>
                      <button
                        style={S.quitar}
                        title="Quitar esta fila"
                        onClick={() => ponerFilas(region, columnas, filas.filter((_, i) => i !== f))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
                {filas.length === 0 && (
                  <tr>
                    <td colSpan={columnas.length + 1} style={{ ...S.td, color: C.tenue, fontSize: 12.5, padding: '10px 9px' }}>
                      Sin filas. En el Word esta tabla saldrá vacía.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <button
            style={S.añadir}
            onClick={() => ponerFilas(region, columnas, filas.concat([filaVacia(columnas)]))}
          >
            + Añadir fila
          </button>
        </>
      );
    }

    if (region.tipo === 'bloque') {
      return (
        <textarea
          style={{ ...S.campo, ...S.area }}
          ref={acomodar}
          value={valor == null ? '' : String(valor)}
          onChange={(e) => { cambiar(region.id, e.target.value); acomodar(e.target); }}
          onBlur={terminado}
        />
      );
    }

    // El folio no se teclea: se reparte al guardar, seguido y sin repetirse. Se
    // enseña igual porque va impreso en el acta y hay que poder leerlo, pero en
    // gris y sin cursor, que es como se ve que no es tuyo.
    if (campoFolio && campoFolio.id === region.id) {
      return (
        <input
          style={{ ...S.campo, ...S.campoFijo }}
          value={folio || (valor == null ? '' : String(valor))}
          readOnly
          tabIndex={-1}
          aria-readonly="true"
          placeholder="Se asigna al guardar"
        />
      );
    }

    // Las fechas, con calendario. Solo si lo que hay escrito se entiende como
    // fecha: si dice «por definir», la caja de texto se queda, porque un
    // selector vacío habría borrado eso de la pantalla.
    const texto = valor == null ? '' : String(valor);
    if (esEtiquetaDeFecha(region.etiqueta) && (!texto.trim() || aISO(texto))) {
      const forma = texto.trim() ? formaDe(texto) : 'larga';
      return (
        <input
          type="date"
          style={{ ...S.campo, ...S.campoFecha }}
          value={aISO(texto)}
          onChange={(e) => { cambiar(region.id, desdeISO(e.target.value, forma)); terminado(); }}
        />
      );
    }

    // Y las horas, con reloj. Mismo criterio que con las fechas: solo si lo
    // escrito se entiende como hora.
    if (esEtiquetaDeHora(region.etiqueta) && (!texto.trim() || aHora24(texto))) {
      return (
        <input
          type="time"
          style={{ ...S.campo, ...S.campoFecha }}
          value={aHora24(texto)}
          onChange={(e) => { cambiar(region.id, desdeHora24(e.target.value)); terminado(); }}
        />
      );
    }

    // El lugar no se escribe: se elige de la lista, y se da de alta con el «+»
    // cuando no está. Escribirlo a mano es lo que producía «Sala de
    // capacitación», «sala capacitacion» y «Sala Capacitación 2do piso» como
    // tres sitios distintos.
    if (campoLugar && campoLugar.id === region.id) {
      return (
        <ComboDeCatalogo
          soloSeleccion
          valor={texto}
          onCambiar={(v) => { cambiar(region.id, v); terminado(); }}
          opciones={catalogoLugares}
          onCrear={onCrearLugar}
          onRenombrar={onRenombrarLugar}
          onOlvidar={onOlvidarLugar}
          estilo={S.campo}
          placeholder="Elige un lugar"
        />
      );
    }

    // Quien convoca es una persona del directorio, y es la misma en veinte
    // actas. Tecleada a mano sale «Ivan Patiño», «Iván A. Patiño» y «Patiño
    // Barragán Iván Aurelio», y entonces buscar lo que convocó alguien deja de
    // funcionar. Mismo selector que el de asignar tareas: se busca escribiendo.
    //
    // Se guarda el nombre y no el identificador, porque lo que va impreso en el
    // .docx es el nombre y el acta tiene que poder leerse sin consultar a nadie.
    // Y se admite escribirlo a mano —«textoLibre»— porque hay actas convocadas
    // por gente de fuera y por áreas enteras.
    if (esEtiquetaDePersona(region.etiqueta)) {
      return (
        <SelectorDePersona
          personas={gente}
          valor=""
          textoMostrado={texto}
          textoLibre
          vacio="Elegir persona"
          etiqueta={region.etiqueta}
          estilo={S.campo}
          onCambio={(id, persona) => {
            cambiar(region.id, persona ? persona.nombre : '');
            terminado();
          }}
        />
      );
    }

    // El objetivo de la reunión no cabe en un renglón: se escribía mirando por
    // una rendija que se desplaza sola, y lo ya escrito dejaba de verse.
    if (esEtiquetaDeTextoLargo(region.etiqueta)) {
      return (
        <textarea
          style={{ ...S.campo, ...S.parrafo }}
          ref={acomodar}
          value={texto}
          onChange={(e) => { cambiar(region.id, e.target.value); acomodar(e.target); }}
          onBlur={terminado}
        />
      );
    }

    return (
      <input
        style={S.campo}
        value={texto}
        onChange={(e) => cambiar(region.id, e.target.value)}
        onBlur={terminado}
      />
    );
  };

  return (
    <div style={S.panel} ref={panel}>
      {/* Sin título ni instrucciones: quien abre su acta ya sabe lo que está
          mirando, y un párrafo explicando que se guarda solo ocupaba más que
          los primeros campos. Queda lo que cambia —si algo se quedó en blanco
          y si está guardado—, alineado a la derecha para que no compita con el
          primer campo. */}
      <div style={S.cabecera}>
        {enBlanco.size > 0 && (
          <span style={S.aviso}>
            {enBlanco.size === 1
              ? '1 campo quedó en blanco'
              : `${enBlanco.size} campos quedaron en blanco`}
          </span>
        )}

        {/* Que se guarde solo no quiere decir que no haya que contarlo: nadie
            confía en un guardado que no da señales. Tres estados y ninguno
            pide nada. */}
        <span style={S.estado(Boolean(error))} role="status">
          {error
            ? error
            : guardando
              ? 'Guardando…'
              : tocado
                ? 'Sin guardar…'
                : (guardadoEn
                  ? `Guardado ${guardadoEn.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Se guarda solo')}
        </span>
      </div>

      {/* Dos columnas para los campos cortos.
      
          «Hora Inicio» ocupando una fila entera obligaba a desplazarse por
          seis pantallas de cajas medio vacías para revisar una cabecera que en
          el Word cabe en tres renglones. Los textos largos y las tablas siguen
          a todo lo ancho: partirlos en dos columnas estrechas los haría
          ilegibles, que es lo contrario de lo que se busca.
      
          Va con hoja de estilo y no con estilos en línea porque hace falta una
          consulta de medios: en una ventana angosta dos columnas son peores
          que una. */}
      <style>{`
        .zonas-v2 {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          column-gap: 18px;
        }
        .zonas-v2 > * { grid-column: span 2; }
        .zonas-v2 > .ancha { grid-column: 1 / -1; }
        .zonas-v2 > .corta { grid-column: span 1; }
        @media (max-width: 1100px) {
          .zonas-v2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>

      <div className="zonas-v2">
      {regiones.map(region => {
        const esTareas = Boolean(zonaTareaDe(region.id));
        return (
          <div
            key={region.id}
            style={S.zona}
            className={claseDeZona(region)}
          >
            <label style={S.etiqueta}>
              <span>{region.etiqueta}</span>
              {enBlanco.has(region.etiqueta) && (
                <span style={S.aviso}>quedó en blanco: no se encontró en la reunión</span>
              )}
              {region.tipo === 'bloque' && (
                <span style={S.pista}>una línea por párrafo · <code>##</code> subtítulo · <code>-</code> viñeta</span>
              )}
              {esTareas && <span style={S.pista}>esta tabla se puede mandar a Planner</span>}
              {asistentes && asistentes.region.id === region.id && directorioSinPuestos && (
                <span style={S.pista}>
                  el directorio no está devolviendo los puestos: leerlos pide un permiso
                  que autoriza el administrador
                </span>
              )}
              {campoFolio && campoFolio.id === region.id && (
                <span style={S.pista}>lo asigna el sistema, consecutivo por año</span>
              )}
            </label>

            {editorDeZona(region)}

            {/* El botón de Planner ya no va aquí: vive en la barra de menús,
                al lado de «Edición», donde está el resto de lo que se HACE con
                el acta. Debajo de la tabla quedaba a una pantalla de distancia
                de todo lo demás, y había que buscarlo. */}
          </div>
        );
      })}
      </div>

      {/* Sin pie. Guardar ya no es un acto: ocurre solo al dejar de escribir,
          y el estado vive arriba, junto al título, que es donde se está
          mirando. Un botón que no hay que pulsar solo ocupa sitio y hace dudar
          de si se ha pulsado. */}

      {planner && tareas.length > 0 && (
        <EnviarAPlanner
          zonas={zonasParaPlanner}
          fechaReunion={fechaReunion}
          tokenGraph={tokenGraph}
          titulo={titulo}
          yaCreadas={tareasCreadas}
          onRegistrar={apuntarTarea}
          onCerrar={() => { setPlanner(false); refrescarTareas(); }}
        />
      )}
    </div>
  );
};

export default ZonasDelActa;
